import { useEffect, useState } from "react";
import { useAdminAuth } from "@/context/AdminAuthContext";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { adminApi, getApiErrorMessage } from "@/lib/api";
import { KeyRound, ShieldAlert, PlugZap } from "lucide-react";

const EMPTY = {
  uat_url: "", prod_url: "", member_code: "", username: "", password: "", mode: "plain",
  fingerprint: "", member_private_pem: "", bse_public_pem: "", verify_ssl: true,
};

export default function BseCredentials() {
  const { admin } = useAdminAuth();
  const isSuper = admin?.role === "super_admin";
  const [current, setCurrent] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const load = () =>
    adminApi.get("/admin/bse2/config").then((r) => {
      setCurrent(r.data);
      if (r.data.configured) {
        setForm((f) => ({
          ...f, uat_url: r.data.uat_url, prod_url: r.data.prod_url, member_code: r.data.member_code,
          username: r.data.username, mode: r.data.mode, fingerprint: r.data.fingerprint,
          verify_ssl: r.data.verify_ssl,
        }));
      }
    }).catch((error) => toast.error(getApiErrorMessage(error)));

  useEffect(() => { if (isSuper) load(); }, [isSuper]);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await adminApi.put("/admin/bse2/config", form);
      setCurrent(data);
      toast.success("BSE credentials saved & applied (token cache reset)");
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const test = async () => {
    setTesting(true);
    try {
      const { data } = await adminApi.post("/admin/bse2/config/test");
      if (data.status === "success") toast.success(data.message);
      else toast.error(`Connection failed: ${data.message}`);
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    } finally {
      setTesting(false);
    }
  };

  if (!isSuper) {
    return (
      <AdminLayout>
        <div className="flex flex-col items-center py-24 text-center" data-testid="bse-creds-denied">
          <ShieldAlert className="h-12 w-12 text-amber-500" />
          <h1 className="mt-4 font-heading text-2xl text-[var(--brand)]">Super Admin only</h1>
          <p className="mt-2 text-sm text-[var(--ink-muted)]">BSE credentials can only be managed by the Super Admin.</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="mb-6 flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-heading text-3xl text-[var(--brand)]">BSE Credentials</h1>
          <p className="text-sm text-[var(--ink-muted)]">Go-live member credentials & JOSE production keys — applied instantly, persisted across deploys.</p>
        </div>
        {current?.configured && (
          <Badge className={current.mode === "jose" ? "bg-emerald-600" : "bg-amber-500"} data-testid="bse-creds-mode-badge">
            {current.mode === "jose" ? "PRODUCTION (JOSE)" : "UAT (plain)"}
          </Badge>
        )}
      </div>

      <Card className="glass-card max-w-3xl border-0">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 font-heading text-lg text-[var(--brand)]">
            <KeyRound className="h-4 w-4" /> Member Credentials
          </CardTitle>
          {current?.updated_at && (
            <p className="text-xs text-[var(--ink-muted)]">Last updated {current.updated_at.slice(0, 16).replace("T", " ")} by {current.updated_by}</p>
          )}
        </CardHeader>
        <CardContent>
          <form className="space-y-4" onSubmit={save}>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="grid grid-cols-1 gap-4 sm:col-span-2 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label className="flex items-center gap-2">UAT Base URL
                    {form.mode === "plain" && <Badge className="bg-amber-500 px-1.5 py-0 text-[10px]">ACTIVE</Badge>}
                  </Label>
                  <Input required={form.mode === "plain"} value={form.uat_url} onChange={upd("uat_url")} placeholder="https://starmfv2demo.bseindia.com/api" data-testid="bse-creds-uat-url" />
                </div>
                <div className="space-y-1.5">
                  <Label className="flex items-center gap-2">Production Base URL
                    {form.mode === "jose" && <Badge className="bg-emerald-600 px-1.5 py-0 text-[10px]">ACTIVE</Badge>}
                  </Label>
                  <Input required={form.mode === "jose"} value={form.prod_url} onChange={upd("prod_url")} placeholder="https://starmfapi.bseindia.com/api" data-testid="bse-creds-prod-url" />
                </div>
                <p className="-mt-2 text-[11px] text-[var(--ink-muted)] sm:col-span-2">Selected Mode decides which URL is live — UAT/Demo mode uses the UAT URL, Production (JOSE) mode uses the Production URL.</p>
              </div>
              <div className="space-y-1.5"><Label>Member code</Label><Input required value={form.member_code} onChange={upd("member_code")} data-testid="bse-creds-member" /></div>
              <div className="space-y-1.5"><Label>Username</Label><Input required value={form.username} onChange={upd("username")} placeholder="member/9236/xxxx" data-testid="bse-creds-username" /></div>
              <div className="space-y-1.5">
                <Label>Password {current?.password_set && <span className="text-[11px] text-emerald-600">(set — leave blank to keep)</span>}</Label>
                <Input type="password" value={form.password} onChange={upd("password")} data-testid="bse-creds-password" />
              </div>
              <div className="space-y-1.5">
                <Label>Mode</Label>
                <select value={form.mode} onChange={upd("mode")} data-testid="bse-creds-mode"
                  className="h-11 w-full rounded-md border border-input bg-transparent px-3 text-base sm:h-9 sm:text-sm">
                  <option value="plain">UAT / Demo (plain JSON)</option>
                  <option value="jose">Production (JOSE encrypted)</option>
                </select>
              </div>
            </div>

            {form.mode === "jose" && (
              <div className="space-y-4 rounded-xl border border-amber-200 bg-amber-50/50 p-4">
                <p className="text-xs font-medium text-amber-700">Production JOSE keys (from BSE key-exchange at go-live)</p>
                <div className="space-y-1.5"><Label>Key fingerprint</Label><Input required value={form.fingerprint} onChange={upd("fingerprint")} data-testid="bse-creds-fingerprint" /></div>
                <div className="space-y-1.5">
                  <Label>Member PRIVATE key (PEM) {current?.member_private_pem_set && <span className="text-[11px] text-emerald-600">(set — paste to replace)</span>}</Label>
                  <Textarea rows={5} value={form.member_private_pem} onChange={upd("member_private_pem")} placeholder="-----BEGIN PRIVATE KEY-----" className="font-mono text-xs" data-testid="bse-creds-privkey" />
                </div>
                <div className="space-y-1.5">
                  <Label>BSE PUBLIC key (PEM) {current?.bse_public_pem_set && <span className="text-[11px] text-emerald-600">(set — paste to replace)</span>}</Label>
                  <Textarea rows={5} value={form.bse_public_pem} onChange={upd("bse_public_pem")} placeholder="-----BEGIN PUBLIC KEY-----" className="font-mono text-xs" data-testid="bse-creds-pubkey" />
                </div>
              </div>
            )}

            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <Button type="submit" disabled={saving} className="rounded-full bg-[var(--brand)]" data-testid="bse-creds-save">
                {saving ? "Saving..." : "Save & Apply"}
              </Button>
              <Button type="button" variant="outline" disabled={testing || !current?.configured} onClick={test} className="rounded-full" data-testid="bse-creds-test">
                <PlugZap className="mr-1 h-4 w-4" /> {testing ? "Testing..." : "Test Connection"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </AdminLayout>
  );
}
